import React, { useEffect, useState } from "react";
import { Card, Table, Button, Placeholder } from "react-bootstrap";
import { TeamResultWithRider, Event } from "../../../types/model";
import { getTeamResultsByEvent } from "../../../services/teamResultService";
import Modal from "../../common/components/ThemedModal";
import getCountryISO2 from "country-iso-3-to-2";
import ReactCountryFlag from "react-country-flag";
import { FaList } from "react-icons/fa";
import { RootState } from "../../../store/store";
import { useAppSelector } from "../../../store/hooks";

interface TeamResultsTableProps {
	event: Event;
}

const TeamResultsTable: React.FC<TeamResultsTableProps> = ({ event }) => {
	const { isMobile } = useAppSelector((state: RootState) => state.user);
	const [teamResults, setTeamResults] = useState<TeamResultWithRider[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [showModal, setShowModal] = useState(false);
	const [selectedTeam, setSelectedTeam] = useState<TeamResultWithRider | null>(
		null,
	);

	const handleViewRiders = (team: TeamResultWithRider) => {
		setSelectedTeam(team);
		setShowModal(true);
	};

	const handleCloseModal = () => {
		setShowModal(false);
		setSelectedTeam(null);
	};

	useEffect(() => {
		if (!event || !event._id) {
			setTeamResults([]);
			return;
		}

		fetchTeamResults();
	}, [event]);

	const fetchTeamResults = async () => {
		try {
			setLoading(true);
			const res = await getTeamResultsByEvent(event._id);
			if (res) {
				const sorted = res.data.sort(
					(a: TeamResultWithRider, b: TeamResultWithRider) => {
						const aPoints = a.points ?? 0;
						const bPoints = b.points ?? 0;
						if (aPoints > bPoints) return -1;
						if (aPoints < bPoints) return 1;
						return 0;
					},
				);
				setTeamResults(sorted);
			}
		} catch (error) {
			console.error("Error fetching team results:", error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<Card className="my-3 shadow bg-secondary text-light border-0">
				{loading ? (
					<Placeholder as="div" animation="glow" className="my-5 mx-3">
						<Placeholder xs={8} bg="light" className="rounded-3 mb-2" />
						<Placeholder
							xs={11}
							className="rounded-3"
							bg="light"
							style={{ height: "13rem" }}
						/>
					</Placeholder>
				) : (
					<Card.Body style={{ maxHeight: "20rem", overflowY: "auto" }}>
						<h4 className="fw-bold  ms-2 d-flex justify-content-between">
							<div className="">FACTORY TEAM RESULTS</div>
							<div className="">
								{event && event.location ? event.location : "N/A"}
								{event && event.country && (
									<ReactCountryFlag
										countryCode={event.country}
										svg
										className="mb-1 ms-2"
										style={{ width: "1.5em", height: "1.5em" }}
									/>
								)}
							</div>
						</h4>
						{isMobile && (
							<div className="fst-italic my-2">Select team to view results</div>
						)}
						{teamResults.length > 0 ? (
							<Table striped bordered hover responsive>
								<thead>
									<tr>
										<th>Team </th>
										{!isMobile && <th>Rider Points</th>}
										<th>{!isMobile && "Fantasy"} Points</th>
										{!isMobile && <th>Actions</th>}
									</tr>
								</thead>
								<tbody>
									{teamResults.map((result: TeamResultWithRider) => (
										<tr
											key={result._id}
											onClick={() => handleViewRiders(result)}
											style={{
												cursor: "pointer",
												transition: "background-color 0.3s",
											}}
										>
											<td>
												{teamResults.indexOf(result) + 1}. {result.name}{" "}
											</td>
											{!isMobile && <td>{result.totalTeamPoints ?? 0}</td>}
											<td>{result.points ?? 0}</td>
											{!isMobile && (
												<td>
													<Button
														variant="success"
														size="sm"
														className="text-light align-items-center d-flex"
														onClick={() => handleViewRiders(result)}
													>
														<FaList className="me-1" />
														RIDERS
													</Button>
												</td>
											)}
										</tr>
									))}
								</tbody>
							</Table>
						) : (
							<p>No team results available for the selected event.</p>
						)}
					</Card.Body>
				)}
			</Card>
			<Modal
				show={showModal}
				onHide={handleCloseModal}
				centered
				
			>
				<Modal.Header
					closeButton
					className="bg-dark text-light border-secondary"
				>
					<Modal.Title className="fw-bold">
						{selectedTeam ? `${selectedTeam.name}` : "Riders"}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body >
					{selectedTeam &&
					selectedTeam.riders &&
					selectedTeam.riders.length > 0 ? (
						<Table striped bordered hover size="sm" responsive>
							<thead>
								<tr>
									<th>Name</th>
									<th>Points</th>
								</tr>
							</thead>
							<tbody>
								{selectedTeam.riders.map((rider) => (
									<tr key={rider.rider._id}>
										<td>
											{getCountryISO2(
												(rider?.rider?.country ?? "").toUpperCase().trim(),
											) ? (
												<ReactCountryFlag
													countryCode={getCountryISO2(
														(rider.rider.country ?? "").toUpperCase().trim(),
													)}
													svg
													style={{ width: "3.5rem" }}
												/>
											) : (
												rider.rider.country
											)}
											{rider.rider.first_name} <b>{rider.rider.last_name}</b>
										</td>
										<td>{rider.points ?? 0}</td>
									</tr>
								))}
							</tbody>
						</Table>
					) : (
						<p>No riders found for this team.</p>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={handleCloseModal}>
						Close
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default TeamResultsTable;
